/**
 * 목표 실적 v2 — 큐브 접기 (순수 함수)
 *
 * `GET /contracts/goals/v2/dashboard?year=` 는 월×지점×그룹×상태 큐브를 한 번에 내려준다.
 * 화면의 필터(기간·지점·그룹·산입기준)는 **재조회 없이 여기서 접는다** — API 파라미터는
 * `year` 하나뿐이다.
 *
 * 이 파일에는 Vue·DOM 참조가 없다. 산술을 마크업과 분리해 따로 검증하기 위함이다.
 */

/** 큐브의 상태 축 순서. 서버 `STATUS_AXIS` 와 같아야 한다 — 인덱스로 참조하므로 순서 고정. */
export const STATUS_KEYS = ['C', 'S', 'E', 'X'];

export const STATUS_LABEL = {
  C: '완료',
  S: '서명중',
  E: '기한경과',
  X: '취소',
};

/** 품목 축. 서버 `itemCube` 의 3~6번 칸 순서와 같다. */
export const ITEM_KEYS = ['implant', 'material', 'equipment', 'etc'];

export const ITEM_LABEL = {
  implant: '임플란트',
  material: '재료',
  equipment: '장비',
  etc: '기타',
  unclassified: '품목 미분류',
};

/**
 * 계약서 유형 → 그룹 표시 매핑.
 *
 * `typeCube` 에는 그룹 축이 없어서 유형 표의 그룹 필터는 이 휴리스틱으로 판정한다.
 * **정본 그룹 축은 `cube` 의 groupIdx 다** — 이건 유형 목록을 영업/장비로 갈라 보여주기
 * 위한 표시용이며, 여기에 없는 유형은 장비로 떨어진다.
 */
const TYPE_GROUP = {
  물품매매: '영업',
  물품매매V4: '영업',
  매출프로모션: '영업',
};

export function typeGroupOf(contractType) {
  return TYPE_GROUP[contractType] || '장비';
}

/** 기본 필터 상태. */
export function defaultFilter() {
  return { m0: 1, m1: 12, dept: -1, group: -1 };
}

/** 기본 산입 기준 — 완료만 산입, 서명중은 켜고 기한경과는 끈다(회수 대상이라 실적이 아니다). */
export function defaultInclusion() {
  return { C: true, S: true, E: false };
}

function inRange(month, filter) {
  return month >= filter.m0 && month <= filter.m1;
}

/** cube 한 행이 필터를 통과하는가. `[월, 지점idx, 그룹idx, 상태idx, 건수, 금액]` */
function passCube(row, filter) {
  return (
    inRange(row[0], filter) &&
    (filter.dept < 0 || row[1] === filter.dept) &&
    (filter.group < 0 || row[2] === filter.group)
  );
}

function emptyBuckets() {
  return { C: [0, 0], S: [0, 0], E: [0, 0], X: [0, 0] };
}

/**
 * 상태별 [건수, 금액] 으로 접는다.
 * `extra` 는 지점 단건 집계처럼 행을 더 좁힐 때 쓰는 술어다.
 */
export function aggregateByStatus(cube, filter, extra) {
  const out = emptyBuckets();
  for (const row of cube) {
    if (!passCube(row, filter)) continue;
    if (extra && !extra(row)) continue;
    const key = STATUS_KEYS[row[3]];
    if (!key) continue;
    out[key][0] += row[4];
    out[key][1] += row[5];
  }
  return out;
}

/**
 * 산입 기준이 켜진 상태만 합산해 [건수, 금액] 을 낸다.
 * 취소(X)는 어떤 경우에도 실적이 아니므로 여기 들어오지 않는다.
 */
export function included(buckets, inclusion) {
  let count = 0;
  let amount = 0;
  for (const key of ['C', 'S', 'E']) {
    if (!inclusion[key]) continue;
    count += buckets[key][0];
    amount += buckets[key][1];
  }
  return [count, amount];
}

/** 완료 기준 월별 금액 12칸. 추세 그래프와 전년 비교의 기준선이다. */
export function monthlyCompleted(cube, filter) {
  const out = new Array(12).fill(0);
  for (const row of cube) {
    if (row[3] !== 0) continue; // COMPLETED 만
    if (filter.dept >= 0 && row[1] !== filter.dept) continue;
    if (filter.group >= 0 && row[2] !== filter.group) continue;
    out[row[0] - 1] += row[5];
  }
  return out;
}

/** 지점별 행. 실적 금액 내림차순, 아무 계약도 없는 지점은 제외한다. */
export function deptRows(cube, depts, filter, inclusion) {
  return depts
    .map((name, idx) => {
      const buckets = aggregateByStatus(cube, filter, (row) => row[1] === idx);
      const [count, amount] = included(buckets, inclusion);
      return {
        idx,
        name,
        count,
        amount,
        expiredCount: buckets.E[0],
        expiredAmount: buckets.E[1],
        canceledCount: buckets.X[0],
        canceledAmount: buckets.X[1],
      };
    })
    .filter((r) => r.count > 0 || r.expiredCount > 0 || r.canceledCount > 0)
    .sort((a, b) => b.amount - a.amount);
}

/** 지점 셀렉트 정렬용 — 완료 금액 기준(필터와 무관하게 연간 전체로 센다). */
export function deptOrderByCompleted(cube, depts) {
  return depts
    .map((name, idx) => {
      let total = 0;
      for (const row of cube) {
        if (row[1] === idx && row[3] === 0) total += row[5];
      }
      return { idx, name, total };
    })
    .sort((a, b) => b.total - a.total);
}

/**
 * 계약서 유형별 행. `typeCube` 는 `[월, 유형, 상태idx, 건수, 금액]`.
 *
 * 지점 축이 없어 **지점 필터가 적용되지 않는다.** 화면이 그 사실을 캡션으로 밝힌다.
 */
export function typeRows(typeCube, filter, inclusion) {
  const map = new Map();
  for (const row of typeCube) {
    const [month, contractType, statusIdx, count, amount] = row;
    if (!inRange(month, filter)) continue;

    const group = typeGroupOf(contractType);
    if (filter.group >= 0 && group !== ['영업', '장비'][filter.group]) continue;

    const key = STATUS_KEYS[statusIdx];
    if (!key) continue;

    let entry = map.get(contractType);
    if (!entry) {
      entry = { name: contractType, group, count: 0, amount: 0, completed: 0, expired: 0 };
      map.set(contractType, entry);
    }
    if (key === 'C') entry.completed += amount;
    if (key === 'E') entry.expired += amount;
    if (inclusion[key]) {
      entry.count += count;
      entry.amount += amount;
    }
  }
  return [...map.values()].filter((e) => e.amount > 0).sort((a, b) => b.amount - a.amount);
}

/**
 * 품목별 금액. `itemCube` 는 `[월, 지점idx, 상태idx, 임플란트, 재료, 장비, 기타, 미분류액, 미분류건]`.
 *
 * [HARD] **미분류를 같은 결과에 실어 돌려준다.** 품목 4칸의 합은 총매출이 아니다 — 계약서 폼이
 * 항목 4칸을 필수로 받지 않아 작성자가 비운 계약이 구조적으로 남고(운영 실측 약 26%),
 * 항목별 저장 이전 계약과 프릭스 계약도 영원히 미분류다. 호출부가 미분류를 빼고 그릴 수
 * 없도록 분리해서 내보내지 않는다.
 *
 * 그룹 축이 없어 **그룹 필터가 적용되지 않는다.**
 */
export function itemBreakdown(itemCube, filter, inclusion) {
  const totals = { implant: 0, material: 0, equipment: 0, etc: 0 };
  let unclassifiedAmount = 0;
  let unclassifiedCount = 0;

  for (const row of itemCube) {
    const [month, deptIdx, statusIdx] = row;
    if (!inRange(month, filter)) continue;
    if (filter.dept >= 0 && deptIdx !== filter.dept) continue;

    const key = STATUS_KEYS[statusIdx];
    if (!key || !inclusion[key]) continue;

    ITEM_KEYS.forEach((itemKey, i) => {
      totals[itemKey] += row[3 + i];
    });
    unclassifiedAmount += row[7];
    unclassifiedCount += row[8];
  }

  const classified = ITEM_KEYS.reduce((sum, k) => sum + totals[k], 0);
  const total = classified + unclassifiedAmount;

  return {
    totals,
    classified,
    unclassifiedAmount,
    unclassifiedCount,
    total,
    /** 품목으로 쪼갤 수 있는 몫의 비중. 화면이 "합계가 총매출이 아니다"를 말할 때 쓴다. */
    coverage: total > 0 ? classified / total : 0,
    /** 막대·표가 그대로 쓰는 5칸. 미분류가 마지막 1급 항목이다. */
    rows: [
      ...ITEM_KEYS.map((k) => ({
        key: k,
        label: ITEM_LABEL[k],
        amount: totals[k],
        share: total > 0 ? totals[k] / total : 0,
      })),
      {
        key: 'unclassified',
        label: ITEM_LABEL.unclassified,
        amount: unclassifiedAmount,
        count: unclassifiedCount,
        share: total > 0 ? unclassifiedAmount / total : 0,
      },
    ],
  };
}

/** 선택 구간의 목표 합. 12칸이 다 등록되지 않았으면 서버가 null 을 주고 여기도 null 이다. */
export function goalInRange(goals, filter) {
  if (!goals) return null;
  let sum = 0;
  for (let m = filter.m0; m <= filter.m1; m += 1) sum += goals[m - 1] || 0;
  return sum;
}

/** 선택 구간의 전년 완료 실적 합. */
export function prevYearInRange(prevYearMonthly, filter) {
  let sum = 0;
  for (let m = filter.m0; m <= filter.m1; m += 1) sum += prevYearMonthly[m - 1] || 0;
  return sum;
}
